import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { IProgressItem, ProgressWidgetProps } from './typings'
import { listStyles } from './styling'
import Button from '@mui/material/Button'
import { ProgressListItem } from './ProgressListItem/ProgressListItem'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { useSwipeable } from 'react-swipeable'
import Grid from '@mui/material/Grid'
import List from '@mui/material/List'
import ButtonGroup from '@mui/material/ButtonGroup'
import { useResize } from '@sales-center/components'

export const ProgressWidget: React.FC<ProgressWidgetProps> = (
  props: ProgressWidgetProps,
) => {
  const {
    scrollTo = 0,
    segmentMinWidth = 100,
    items,
    snap = true,
    opacity = 1,
    activeSegmentIndex,
    onActiveIndexChange,
    isTidalEnabled,
  } = props

  // ========== S T A T E ==========
  const [showPageButtons, setShowPageButtons] = useState<boolean>(true)
  const [scrollAmount, setScrollAmount] = useState<number>(0)

  // ========== M A T E R I A L   U I   S T Y L E S ======

  const ref = useRef<HTMLDivElement>(null)

  const { width: containerWidth, marginToNextElement } = useResize(ref)

  const pageButtonsWidth = isTidalEnabled ? 100 : 110
  const marginLeft = -Math.ceil(props.height / 2) + props.segmentSpacing
  const totalMarginAmount =
    Math.max(0, items.length - 1) * marginLeft + marginToNextElement
  const segmentWidth: number | undefined = containerWidth
    ? Math.max(
        segmentMinWidth,
        (containerWidth -
          totalMarginAmount -
          (showPageButtons ? pageButtonsWidth : 0)) /
          items.length,
      )
    : undefined
  const segmentEffectiveWidth = segmentWidth ? segmentWidth + marginLeft : 0
  const minScrollAmount =
    containerWidth && segmentWidth
      ? -(
          segmentWidth * items.length +
          totalMarginAmount +
          pageButtonsWidth -
          containerWidth
        )
      : 0

  // ========== H O O K S ==========
  useEffect(() => {
    const show: boolean = containerWidth
      ? items.length * segmentMinWidth > containerWidth
      : false
    setShowPageButtons(show)
    if (scrollAmount < minScrollAmount) {
      setScrollAmount(minScrollAmount)
    }
    if (!show) {
      setScrollAmount(0)
    }
  }, [
    containerWidth,
    items.length,
    minScrollAmount,
    scrollAmount,
    segmentMinWidth,
  ])

  useEffect(() => {
    if (scrollTo) {
      if (typeof scrollTo === 'number') {
        // set scroll so item with index is centered
        const newScrollAmount = Math.max(
          minScrollAmount,
          -(scrollTo * segmentEffectiveWidth) +
            (containerWidth ? containerWidth / 2 : 0) -
            segmentEffectiveWidth / 2,
        )
        setScrollAmount(Math.min(newScrollAmount, 0))
      } else {
        switch (scrollTo) {
          case 'left':
            setScrollAmount(0)
            break
          case 'right':
            setScrollAmount(minScrollAmount)
            break
        }
      }
    }
  }, [
    containerWidth,
    items.length,
    minScrollAmount,
    scrollTo,
    segmentEffectiveWidth,
  ])

  // ========== E V E N T   H A N D L E R S ==========

  const handleCustomHandlerClose = useCallback(() => {
    console.log('close')
  }, [])

  const handleItemClick = useCallback(
    (
      event: React.MouseEvent<HTMLLIElement, MouseEvent>,
      item: IProgressItem,
      index: number,
    ) => {
      // if user-defined click handler (e.g. open closed/win/lost dialog)
      if (item.onClick) {
        item.onClick(event, handleCustomHandlerClose)
        onActiveIndexChange(index)
      } else {
        props.onChange(item.value)
        onActiveIndexChange(null)
      }
    },
    [handleCustomHandlerClose, onActiveIndexChange, props],
  )

  const handleScrollLeftButtonClick = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event?.stopPropagation()
      let offset = snap
        ? segmentEffectiveWidth - (scrollAmount % segmentEffectiveWidth)
        : segmentEffectiveWidth
      if (offset === 0) {
        offset = segmentEffectiveWidth
      }
      const newScrollAmount = Math.min(0, scrollAmount + offset)
      setScrollAmount(newScrollAmount)
    },
    [scrollAmount, segmentEffectiveWidth, snap],
  )

  const handleScrollRightButtonClick = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event?.stopPropagation()
      let offset = snap
        ? scrollAmount % segmentEffectiveWidth
        : segmentEffectiveWidth
      if (offset === 0) {
        offset = segmentEffectiveWidth
      }
      const newScrollAmount = Math.max(minScrollAmount, scrollAmount - offset)
      setScrollAmount(newScrollAmount)
    },
    [minScrollAmount, scrollAmount, segmentEffectiveWidth, snap],
  )

  const handlers = useSwipeable({
    onSwipedLeft: (eventData) => {
      eventData.event.preventDefault()
      handleScrollRightButtonClick()
    },
    onSwipedRight: (eventData) => {
      eventData.event.preventDefault()
      handleScrollLeftButtonClick()
    },
  })

  return (
    <Grid gap={1} container ref={ref} wrap="nowrap">
      {containerWidth ? (
        <>
          <Grid
            item
            sx={{
              pointerEvents: props.disabled ? 'none' : 'auto',
              opacity: props.disabled ? 0.5 : opacity,
              overflow: 'hidden',
              width: '100%',
              position: 'relative',
              transition: 'opacity 0.5s',
            }}
            {...handlers}
          >
            <List
              sx={{
                ...listStyles.root,
                position: 'relative',
                transition: 'left 0.25s',
                left: scrollAmount,
                top: 0,
              }}
              dense
              disablePadding
            >
              {items.map((item, index) => (
                <ProgressListItem
                  key={index}
                  color={
                    item.isComplete
                      ? props.colors.completeColors.backgroundColor
                      : props.colors.incompleteColors.backgroundColor
                  }
                  colorHover={
                    item.isComplete
                      ? props.colors.completeColors.backgroundColorHover
                      : props.colors.incompleteColors.backgroundColorHover
                  }
                  disabled={props.disabled}
                  endAdornment={item.endAdornment}
                  marginLeft={index === 0 ? 0 : marginLeft}
                  onClick={(event) => handleItemClick(event, item, index)}
                  open={activeSegmentIndex === index}
                  padding={index === 0 ? '0 12px' : '0 34px'}
                  text={item.text}
                  textColor={
                    item.isComplete
                      ? props.colors.completeColors.textColor
                      : props.colors.incompleteColors.textColor
                  }
                  textColorHover={
                    item.isComplete
                      ? props.colors.completeColors.textColorHover
                      : props.colors.incompleteColors.textColorHover
                  }
                  transitionTime={props.transitionTime}
                  value={item.value}
                  variant={
                    items.length === 1
                      ? 'single'
                      : index === 0
                        ? 'left'
                        : index === items.length - 1
                          ? 'right'
                          : 'middle'
                  }
                  height={props.height}
                  width={segmentWidth}
                  dataTestId={item.dataTestId}
                ></ProgressListItem>
              ))}
            </List>
          </Grid>

          {/* ========== S C R O L L   B U T T O N S ========== */}
          {showPageButtons && (
            <Grid item textAlign="right">
              <ButtonGroup
                color="primary"
                disabled={props.disabled}
                aria-label="outlined primary button group"
                size="small"
              >
                <Button
                  aria-label="scroll left"
                  color="primary"
                  disabled={scrollAmount >= 0}
                  variant="outlined"
                  sx={{ height: 32, px: 1.5 }}
                  onClick={handleScrollLeftButtonClick}
                  data-test-id="progressWidgetScrollLeftButton"
                >
                  <ChevronLeft
                    sx={{
                      height: '20px',
                      width: '20px',
                    }}
                  />
                </Button>
                <Button
                  aria-label="scroll right"
                  disabled={scrollAmount <= minScrollAmount}
                  color="primary"
                  variant="outlined"
                  sx={{ height: 32, px: 1.5 }}
                  onClick={handleScrollRightButtonClick}
                  data-test-id="progressWidgetScrollRightButton"
                >
                  <ChevronRight
                    sx={{
                      height: '20px',
                      width: '20px',
                    }}
                  />
                </Button>
              </ButtonGroup>
            </Grid>
          )}
        </>
      ) : (
        <></>
      )}
    </Grid>
  )
}

ProgressWidget.displayName = 'ProgressWidget'
